import React, { useState, useEffect } from 'react';
import { useClient } from 'jsonapi-react';
import { Loader } from '@/components/custom/loader';
import toast from 'react-hot-toast';
import { EmptyState } from '@/components/custom/empty-state';
import { CourseClassroomCommentsCard } from './course-classroom-comments-card';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { PostCardWrapper, PostCommentList } from '@/components/custom/post';
import { confirm } from '@/lib/confirm';

export function CourseClassroomComments(props) {
  const { t } = useTranslation();
  const { selectedItemId } = props;
  const client = useClient();
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');

  const createComment = async () => {
    const { error } = await client.mutate('comments', {
      attributes: {
        body: text,
        'learning-system-item-id': selectedItemId
      }
    });

    if (error) {
      toast.error(t('toast.errorCreateComment'));
    } else {
      toast.success(t('toast.successCreateComment'));
      getComments(selectedItemId);
    }
  };

  const getComments = async selectedItemId => {
    setLoading(true);
    const url = `comments?filter[learning_system_item_id]=${selectedItemId}`;
    const { data, error } = await client.fetch(url);

    setLoading(false);
    if (error) {
      toast.error(t('toast.errorGetComment'));
    } else {
      setComments(data);
    }
  };

  const deleteComment = async (commentToDelete, setIsCommentLoading) => {
    const confirmed = await confirm({
      title: 'Deseja mesmo excluir este comentário?',
      description: 'Esta ação não poderá ser desfeita',
      confirmText: 'Sim',
      cancelText: 'Não'
    });
    if (confirmed) {
      if (setIsCommentLoading) setIsCommentLoading(true);

      try {
        const { error } = await client.delete(['comments', commentToDelete?.id]);

        if (error) {
          toast.error(t('toast.errorDeleteComment'));
        } else {
          toast.success(t('toast.successDeleteComment'));
          getComments(selectedItemId);
        }
      } catch (e) {
        toast.error(`Erro ao excluir mensagem: ${e.message || e}`);
        console.error('Erro na exclusão:', e);
      } finally {
        if (setIsCommentLoading) setIsCommentLoading(false);
      }
    }
  };

  useEffect(() => {
    if (selectedItemId) {
      getComments(selectedItemId);
    }
  }, [selectedItemId]);

  const commentsFiltered = comments.filter(comment => comment['comment-parent-id'] === null);

  return (
    <PostCardWrapper>
      <div className="w-full">
        <Input
          className="bg-bright"
          placeholder={t('textsCommon.placeholderSendYourComment')}
          aria-label={t('textsCommon.placeholderSendYourComment')}
          value={text}
          disabled={loading}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              createComment(text);
              setText('');
            }
          }}
        />
        <p className="mb-0 text-right text-xs text-neutral">{t('textsCommon.textPressEnter')}</p>
      </div>

      <PostCommentList className="mb-0 border-t-0 pt-0">
        {loading ? (
          <Loader />
        ) : commentsFiltered.length > 0 ? (
          commentsFiltered.map(c => (
            <CourseClassroomCommentsCard
              key={c.id}
              comment={c}
              comments={comments}
              deleteCommentFromParent={deleteComment}
              learningSystemItemId={selectedItemId}
            />
          ))
        ) : (
          <EmptyState
            type="content"
            text={t('emptyState.lmsCommentNot')}
            variant="plain"
          />
        )}
      </PostCommentList>
    </PostCardWrapper>
  );
}
